// Audit log hooks (audit_search.rs).
//   POST /v1/workspaces/{ws}/audit/events/search
//   GET  /v1/workspaces/{ws}/audit/events/{eventId}
//   GET  /v1/workspaces/{ws}/audit/events/facet-values/{actors|targets}?q=

import type { FilterValuesResponse } from "@pej/kore/components";
import { keepPreviousData, type MutationCacheConfig, useQuery } from "@tanstack/react-query";
import { ApiError } from "./error";
import { http } from "./http";
import { auditKeys } from "./keys";
import type { AuditEventRs, AuditPageRs, AuditSearchRq } from "./types";

export interface UseAuditSearchOptions {
    enabled?: boolean;
    /** Hold the previous page during a refetch. Off for per-target timelines, where it would show another target's rows. */
    keepPrevious?: boolean;
}

export function useAuditSearch(
    workspaceId: string,
    rq: AuditSearchRq,
    { enabled = true, keepPrevious = true }: UseAuditSearchOptions = {},
) {
    return useQuery({
        queryKey: auditKeys.search(workspaceId, rq),
        queryFn: () => http.post<AuditPageRs>(`/v1/workspaces/${workspaceId}/audit/events/search`, { json: rq }),
        enabled: enabled && Boolean(workspaceId),
        placeholderData: keepPrevious ? keepPreviousData : undefined,
    });
}

/** One event; `null` when the server answers 404. */
export function useAuditEvent(workspaceId: string, eventId: string | null) {
    return useQuery({
        queryKey: auditKeys.event(workspaceId, eventId ?? ""),
        queryFn: async () => {
            try {
                return await http.get<AuditEventRs>(`/v1/workspaces/${workspaceId}/audit/events/${eventId}`);
            } catch (err) {
                if (err instanceof ApiError && err.status === 404) return null;
                throw err;
            }
        },
        enabled: Boolean(workspaceId) && eventId !== null,
    });
}

/** `valuesEndpoint` is relative to the workspace, as the manifest declares it. */
export function fetchAuditFacetValues(
    workspaceId: string,
    valuesEndpoint: string,
    q: string,
): Promise<FilterValuesResponse> {
    const query = q ? `?q=${encodeURIComponent(q)}` : "";
    return http.get<FilterValuesResponse>(`/v1/workspaces/${workspaceId}/${valuesEndpoint}${query}`);
}

type MutationSuccess = NonNullable<MutationCacheConfig["onSuccess"]>;

/** `MutationCache` onSuccess: every write may have recorded an event. Not awaited, so a save never waits on an audit refetch. */
export const invalidateAuditLog: MutationSuccess = (_data, _variables, _result, _mutation, context) => {
    void context.client.invalidateQueries({ queryKey: auditKeys.all });
};
